<svelte:options runes={false} />
<script>
  // Child blotter (TASK-20260823-twap-strategies-tca): every slice with its status, maker/taker chip,
  // posture, limit vs fill, fee saved, pace offset and latency. Sortable by keyboard-operable header
  // buttons; tabular numerals; signed figures carry sign + arrow. v1 is not virtualized (>1,000-row
  // blotters are an explicit non-goal of the task).
  import { blotterRows, sortRows, STATUS_LABELS } from '../../../lib/twapReport.js';
  import { isRejected, reasonLabel } from '../../../lib/twapRejection.js';

  export let children = [];
  export let parent = null;

  let sortKey = 'cycleIndex';
  let sortDir = 'asc';

  const columns = [
    { key: 'cycleIndex', label: '#', numeric: true },
    { key: 'placedAt', label: 'Placed', numeric: false },
    { key: 'status', label: 'Status', numeric: false },
    { key: 'makerLabel', label: 'Liquidity', numeric: false },
    { key: 'postureName', label: 'Posture', numeric: false },
    { key: 'limitPrice', label: 'Limit', numeric: true },
    { key: 'avgFillPrice', label: 'Avg fill', numeric: true },
    { key: 'filledQty', label: 'Filled / target', numeric: true },
    { key: 'improvementVsMidBps', label: 'vs mid', numeric: true },
    { key: 'fees', label: 'Fees', numeric: true },
    { key: 'feeSaved', label: 'Fee saved', numeric: true },
    { key: 'paceBandOffsetPct', label: 'Pace', numeric: true },
    { key: 'latencyMs', label: 'Latency', numeric: true },
  ];

  function sortBy(key) {
    if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    else { sortKey = key; sortDir = columns.find((c) => c.key === key)?.numeric ? 'desc' : 'asc'; }
  }

  $: rows = sortRows(blotterRows(children, parent), sortKey, sortDir);

  const time = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour12: false }) : '—');
  const latency = (ms) => (ms === null || ms === undefined ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
  // A REJECTION is not a cancel: a cancel/expiry at a cycle deadline is normal, a rejection means the
  // slice could not be placed at all. Rendering both muted grey is what made a permanently-unfundable
  // slice read as "In Queue" (TASK-20260825 A1).
  const statusClass = (s) =>
    s === 'Filled' ? 'ok' : isRejected(s) ? 'bad' : /Cancel|Expired/i.test(s ?? '') ? 'off' : 'wait';
</script>

<div class="cb">
  {#if !children || children.length === 0}
    <p class="cb-empty">No slices placed yet.</p>
  {:else}
    <div class="cb-scroll">
      <table class="cb-table">
        <thead>
          <tr>
            {#each columns as c}
              <th scope="col" class:num={c.numeric} aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button type="button" class="cb-sort" on:click={() => sortBy(c.key)}>
                  {c.label}{#if sortKey === c.key}<span aria-hidden="true"> {sortDir === 'asc' ? '↑' : '↓'}</span>{/if}
                </button>
              </th>
            {/each}
          </tr>
        </thead>
        <tbody>
          {#each rows as r (r.childOrderId)}
            <tr>
              <td class="num">{r.cycleIndex + 1}</td>
              <td title={r.childOrderId}>{time(r.placedAt)}</td>
              <td>
                <span class={`cb-chip cb-status-${statusClass(r.status)}`}>{STATUS_LABELS[r.status] ?? r.status}</span>
                {#if isRejected(r.status) && reasonLabel(r.notes)}
                  <span class="cb-reason" title={reasonLabel(r.notes)}>{reasonLabel(r.notes)}</span>
                {/if}
              </td>
              <td><span class={`cb-chip cb-liq-${r.makerLabel}`} title={r.makerSource ?? 'maker flag unknown'}>{r.makerLabel}</span></td>
              <td>{r.postureName ?? '—'}</td>
              <td class="num">{r.limitText}</td>
              <td class="num">{r.avgFillText}</td>
              <td class="num">{r.filledText}</td>
              <td class="num">{r.vsMidText}</td>
              <td class="num">{r.feesText}</td>
              <td class="num">{r.feeSavedText}</td>
              <td class="num">{r.paceText}</td>
              <td class="num">{latency(r.latencyMs)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>

<style>
  .cb { width: 100%; min-width: 0; }
  .cb-scroll { overflow-x: auto; border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); }
  .cb-table { width: 100%; border-collapse: collapse; font-size: var(--font-size-xs, 0.7rem); }
  .cb-table th { position: sticky; top: 0; background: var(--bg-quaternary, #212226); color: var(--text-secondary, #8a8b8d); text-align: left; padding: 0; white-space: nowrap; }
  .cb-table th.num, .cb-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .cb-sort { all: unset; cursor: pointer; padding: 0.4rem 0.5rem; display: block; width: 100%; box-sizing: border-box; font-weight: var(--font-weight-medium, 500); }
  .cb-table th.num .cb-sort { text-align: right; }
  .cb-sort:focus-visible { outline: 2px solid var(--color-primary, #00d4ff); outline-offset: -2px; }
  .cb-table td { padding: 0.35rem 0.5rem; border-top: 1px solid var(--border-primary, #1e1f22); white-space: nowrap; color: var(--text-primary, #fff); }
  .cb-chip { font-size: var(--font-size-xs, 0.7rem); padding: 0.05rem 0.45rem; border-radius: 999px; border: 1px solid var(--border-secondary, #2a2b2e); }
  .cb-status-ok { color: var(--color-success, #00ff88); border-color: var(--color-success, #00ff88); }
  .cb-status-off { color: var(--text-secondary, #8a8b8d); }
  .cb-status-wait { color: var(--color-warning, #ffaa00); border-color: var(--color-warning, #ffaa00); }
  .cb-status-bad { color: var(--color-danger, #ff4d4f); border-color: var(--color-danger, #ff4d4f); }
  .cb-reason { display: block; margin-top: 2px; font-size: 0.75rem; color: var(--color-danger, #ff4d4f); }
  .cb-liq-maker { color: var(--color-success, #00ff88); border-style: dashed; }
  .cb-liq-taker { color: var(--color-danger, #ff3b3b); border-color: var(--color-danger, #ff3b3b); }
  .cb-liq-unknown { color: var(--text-muted, #6a6b6d); }
  .cb-empty { color: var(--text-muted, #6a6b6d); font-size: var(--font-size-sm, 0.85rem); }
</style>
